import { useMemo, useRef, useEffect, useCallback, useState } from "react";
import { format, isBefore } from "date-fns";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";
import { wouldSlotCreateGap } from "@/lib/gapProtection";
import { AlertCircle, CheckSquare } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { MultiSelectSlot } from "@/hooks/useMultiSelect";
import { type PricingRule, type SpecialPriceDate, calculateBookingPrice } from "@/lib/pricing";
import { useTranslation } from "react-i18next";
// UPPDRAG-15: court-sponsor stack in the per-court header.
import { activeSponsorsSorted, type CourtSponsorRow } from "@/lib/courtSponsorHelpers";

interface CourtInfo {
  id: string;
  name: string;
  court_type?: string;
  surface_type?: string | null;
  max_players?: number;
}

interface BookingInfo {
  id: string;
  court_id: string;
  start_time: string;
  end_time: string;
  status: string;
  user_id: string;
  is_open_match?: boolean;
  activity_id?: string | null;
}

/**
 * TASK-75 PR-D Layer 1 — placed league_matches surfaced to BookingGrid.
 *
 * scheduled_date is a YYYY-MM-DD string; scheduled_time is a HH:MM(:SS)
 * string from a time-of-day column. The duration comes from the joined
 * leagues.match_duration_minutes (PostgREST returns the join as either
 * a single object or an array of one — accept both shapes defensively).
 */
export interface LeagueMatchInfo {
  id: string;
  court_id: string | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  status: string | null;
  leagues:
    | { id: string; match_duration_minutes: number | null; hall_id?: string | null }
    | { id: string; match_duration_minutes: number | null; hall_id?: string | null }[]
    | null;
}

interface BookingGridProps {
  courts: CourtInfo[];
  bookings: BookingInfo[];
  hallHours: { start: number; end: number };
  selectedDate: Date;
  userId?: string;
  timeStepMinutes: number;
  onSlotClick: (courtId: string, startTime: Date, isShift?: boolean) => void;
  selectedSlots?: Array<{ courtId: string; startTime: Date }>;
  onJoinWaitlist?: (courtId: string, startTime: Date, endTime: Date) => void;
  onBookingClick?: (booking: BookingInfo) => void;
  /**
   * UPPDRAG-15: optional map of court_id → active sponsors. When
   * provided, the per-court header renders up to 2 sponsor logos with
   * a "+N" badge for any additional sponsors. Caller is responsible
   * for fetching + filtering by active window (use activeSponsorsSorted
   * helper).
   */
  courtSponsors?: Record<string, CourtSponsorRow[]>;
  gapProtection?: {
    enabled: boolean;
    minGapMinutes: number;
    defaultDuration: number;
  };
  pricingRules?: PricingRule[];
  specialDates?: SpecialPriceDate[];
  hallId?: string;
  categorySlug?: string;
  multiSelectEnabled?: boolean;
  multiSelectedSlots?: MultiSelectSlot[];
  onMultiSelectToggle?: (courtId: string, startTime: Date) => void;
  onMultiSelectDragStart?: (courtId: string, startTime: Date) => void;
  onMultiSelectDragContinue?: (courtId: string, startTime: Date) => void;
  onMultiSelectDragEnd?: () => void;
  multiSelectIsDragging?: boolean;
  multiSelectIsAtLimit?: boolean;
  /**
   * TASK-75 PR-D Layer 1 — placed league_matches for the current day +
   * hall. Rendered as purple visually-blocked overlays so customers can't
   * try to book over them. Layer 4 (DB trigger) is the actual guard.
   */
  leagueMatches?: LeagueMatchInfo[];
}

const FIXED_HOUR_WIDTH = 120; // px per hour on mobile (scrollable)
const COURT_LABEL_WIDTH_MOBILE = 80; // px for court name column on mobile
const COURT_LABEL_WIDTH_DESKTOP = 128; // px for court name column on desktop (w-32)
const COURT_HEIGHT = 56; // px per court row
const MOBILE_COURT_HEIGHT = 48;

export default function BookingGrid({
  courts,
  bookings,
  hallHours,
  selectedDate,
  userId,
  timeStepMinutes,
  onSlotClick,
  onJoinWaitlist,
  gapProtection,
  pricingRules,
  specialDates,
  hallId,
  categorySlug,
  onBookingClick,
  selectedSlots = [],
  multiSelectEnabled = false,
  multiSelectedSlots = [],
  onMultiSelectToggle,
  onMultiSelectDragStart,
  onMultiSelectDragContinue,
  onMultiSelectDragEnd,
  multiSelectIsDragging = false,
  multiSelectIsAtLimit = false,
  courtSponsors,
  leagueMatches = [],
}: BookingGridProps) {
  const isMobile = useIsMobile();
  const { t } = useTranslation("booking");
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const rowHeight = isMobile ? MOBILE_COURT_HEIGHT : COURT_HEIGHT;

  // Total hours
  const totalHours = hallHours.end - hallHours.start;

  // Measure container for responsive hour width
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const courtLabelWidth = isMobile ? COURT_LABEL_WIDTH_MOBILE : COURT_LABEL_WIDTH_DESKTOP;

  // On desktop: fit all hours in available width; on mobile: fixed width with scroll
  const hourWidth = useMemo(() => {
    if (isMobile || containerWidth === 0) return FIXED_HOUR_WIDTH;
    const available = containerWidth - courtLabelWidth;
    const fitted = available / totalHours;
    // Use fitted width, but ensure minimum readability
    return Math.max(fitted, 40);
  }, [isMobile, containerWidth, totalHours, courtLabelWidth]);

  const totalWidth = totalHours * hourWidth;

  // Auto-scroll to current time on mount
  useEffect(() => {
    if (!scrollRef.current) return;
    const now = new Date();
    const currentHour = now.getHours();
    if (currentHour >= hallHours.start && currentHour < hallHours.end) {
      const offset = (currentHour - hallHours.start) * hourWidth - (isMobile ? 40 : 120);
      scrollRef.current.scrollLeft = Math.max(0, offset);
    }
  }, [hallHours.start, hallHours.end, isMobile, hourWidth]);

  // Build hour labels
  const hourLabels = useMemo(() => {
    return Array.from({ length: totalHours }, (_, i) => ({
      hour: hallHours.start + i,
      label: `${String(hallHours.start + i).padStart(2, "0")}:00`,
    }));
  }, [totalHours, hallHours.start]);

  // Calculate price per hour slot
  const slotPrices = useMemo(() => {
    if (!pricingRules || !hallId) return new Map<number, { perHour: number; isPeak: boolean }>();
    const prices = new Map<number, { perHour: number; isPeak: boolean }>();
    const cat = categorySlug ?? "standard";

    for (let h = hallHours.start; h < hallHours.end; h++) {
      const slotTime = new Date(selectedDate);
      slotTime.setHours(h, 0, 0, 0);
      const price = calculateBookingPrice(pricingRules, hallId, slotTime, 60, cat, specialDates ?? []);
      if (price.perHour != null) {
        prices.set(h * 60, { perHour: price.perHour, isPeak: price.isPeak });
      }
    }
    return prices;
  }, [pricingRules, hallId, selectedDate, hallHours, specialDates, categorySlug]);

  // Get bookings for a court
  const getCourtBookings = useCallback(
    (courtId: string) => {
      return bookings.filter(
        (b) => b.court_id === courtId && b.status !== "cancelled"
      );
    },
    [bookings]
  );

  // TASK-75 PR-D Layer 1 — normalize league_matches into [start, end) Date
  // pairs filtered to selectedDate, ready for cell-overlap checks. Filtering
  // is also done at the query layer; the date guard here is a safety net for
  // any caller that passes through a wider window.
  const normalizedLeagueMatches = useMemo(() => {
    const targetDate = format(selectedDate, "yyyy-MM-dd");
    type Norm = { id: string; courtId: string; start: Date; end: Date };
    const out: Norm[] = [];
    for (const lm of leagueMatches) {
      if (!lm.court_id || !lm.scheduled_date || !lm.scheduled_time) continue;
      if (lm.scheduled_date !== targetDate) continue;
      // Pull the joined league row (PostgREST may surface as object or [object]).
      const league = Array.isArray(lm.leagues) ? lm.leagues[0] : lm.leagues;
      const duration = league?.match_duration_minutes ?? 90;
      // scheduled_time is a "HH:MM" or "HH:MM:SS" string from a time column.
      const [hh, mm] = lm.scheduled_time.split(":").map((s) => parseInt(s, 10));
      if (Number.isNaN(hh) || Number.isNaN(mm)) continue;
      const start = new Date(selectedDate);
      start.setHours(hh, mm, 0, 0);
      const end = new Date(start.getTime() + duration * 60_000);
      out.push({ id: lm.id, courtId: lm.court_id, start, end });
    }
    return out;
  }, [leagueMatches, selectedDate]);

  // Per-court list (cheap O(courts × matches) build, used by render +
  // click-guard).
  const getCourtLeagueMatches = useCallback(
    (courtId: string) => normalizedLeagueMatches.filter((m) => m.courtId === courtId),
    [normalizedLeagueMatches]
  );

  // Range-overlap check matching the bookings-overlap pattern:
  // (a.start < b.end) && (a.end > b.start). Caller passes the probed
  // booking's [start, end) window.
  const isLeagueMatchOverlap = useCallback(
    (courtId: string, slotStart: Date, slotEnd: Date) => {
      const matches = getCourtLeagueMatches(courtId);
      return matches.some((m) => slotStart < m.end && slotEnd > m.start);
    },
    [getCourtLeagueMatches]
  );

  // Convert time to x position
  const timeToX = useCallback(
    (date: Date) => {
      const hours = date.getHours() + date.getMinutes() / 60;
      return (hours - hallHours.start) * hourWidth;
    },
    [hallHours.start, hourWidth]
  );

  // Compute gap-blocked slots for visual indication
  const gapBlockedSlots = useMemo(() => {
    if (!gapProtection?.enabled || gapProtection.minGapMinutes <= 0) return new Set<string>();
    const blocked = new Set<string>();
    const openTime = `${String(hallHours.start).padStart(2, "0")}:00`;
    const closeTime = `${String(hallHours.end).padStart(2, "0")}:00`;
    const totalSlots = totalHours * (60 / timeStepMinutes);

    for (const court of courts) {
      for (let i = 0; i < totalSlots; i++) {
        const slotMinutes = hallHours.start * 60 + i * timeStepMinutes;
        const slotDate = new Date(selectedDate);
        slotDate.setHours(Math.floor(slotMinutes / 60), slotMinutes % 60, 0, 0);

        if (isBefore(slotDate, new Date())) continue;

        // Check if already booked
        const courtBkgs = getCourtBookings(court.id);
        const isBooked = courtBkgs.some((b) => {
          const bStart = new Date(b.start_time);
          const bEnd = new Date(b.end_time);
          return slotDate >= bStart && slotDate < bEnd;
        });
        if (isBooked) continue;

        if (wouldSlotCreateGap(
          court.id,
          slotDate,
          gapProtection.defaultDuration,
          bookings,
          openTime,
          closeTime,
          gapProtection.minGapMinutes
        )) {
          blocked.add(`${court.id}-${slotMinutes}`);
        }
      }
    }
    return blocked;
  }, [gapProtection, courts, bookings, hallHours, selectedDate, timeStepMinutes, totalHours, getCourtBookings]);

  // PR-AJ Layer C — duration used for range-overlap check.
  // Use the org's gap-protection default if configured (matches wizard's
  // probable initial duration), else fall back to the slot step-size as a
  // conservative minimum. The wizard re-validates the actual chosen duration
  // server-side via validateCourtAvailability + DB trigger.
  const probeDurationMinutes = gapProtection?.defaultDuration ?? timeStepMinutes;

  // Helper: snap an x-coordinate within a row to a slot Date + check if booked
  const snapXToSlot = useCallback(
    (courtId: string, clientX: number, rowRect: DOMRect) => {
      const clickX = clientX - rowRect.left;
      const clickHour = hallHours.start + clickX / hourWidth;
      const totalMinutes = clickHour * 60;
      const snapped = Math.floor(totalMinutes / timeStepMinutes) * timeStepMinutes;
      const snappedDate = new Date(selectedDate);
      snappedDate.setHours(Math.floor(snapped / 60), snapped % 60, 0, 0);

      if (isBefore(snappedDate, new Date())) return null;

      const courtBookings = getCourtBookings(courtId);
      // PR-AJ Layer C — range-overlap check (was: start-only).
      const probeEnd = new Date(snappedDate.getTime() + probeDurationMinutes * 60_000);
      const isBooked = courtBookings.some((b) => {
        const bStart = new Date(b.start_time);
        const bEnd = new Date(b.end_time);
        return snappedDate < bEnd && probeEnd > bStart;
      });

      // TASK-75 PR-D Layer 1 — treat league-match overlap exactly like a
      // booking overlap for the purpose of suppressing book/multi-select.
      const isLeague = isLeagueMatchOverlap(courtId, snappedDate, probeEnd);

      return { snappedDate, isBooked: isBooked || isLeague, isLeague };
    },
    [hallHours.start, hourWidth, timeStepMinutes, selectedDate, getCourtBookings, probeDurationMinutes, isLeagueMatchOverlap]
  );

  // Document-level mouseup listener for ending drag
  useEffect(() => {
    if (!multiSelectEnabled || !onMultiSelectDragEnd) return;
    const handleMouseUp = () => {
      if (multiSelectIsDragging) {
        onMultiSelectDragEnd();
      }
    };
    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [multiSelectEnabled, multiSelectIsDragging, onMultiSelectDragEnd]);

  // Handle click on empty area
  const handleRowClick = useCallback(
    (courtId: string, e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickHour = hallHours.start + clickX / hourWidth;
      
      // Snap to nearest step
      const totalMinutes = clickHour * 60;
      const snapped = Math.floor(totalMinutes / timeStepMinutes) * timeStepMinutes;
      const snappedDate = new Date(selectedDate);
      snappedDate.setHours(Math.floor(snapped / 60), snapped % 60, 0, 0);

      if (isBefore(snappedDate, new Date())) return;

      // PR-AJ Layer C — range-overlap check (was: start-only).
      // Catches cases where a probe-duration booking would cross into a block.
      const courtBookings = getCourtBookings(courtId);
      const probeEnd = new Date(snappedDate.getTime() + probeDurationMinutes * 60_000);
      const isBooked = courtBookings.some((b) => {
        const bStart = new Date(b.start_time);
        const bEnd = new Date(b.end_time);
        // Standard interval-overlap: (a.start < b.end) && (a.end > b.start)
        return snappedDate < bEnd && probeEnd > bStart;
      });
      if (isBooked) return;

      // TASK-75 PR-D Layer 1 — league-match overlap blocks the booking flow
      // and surfaces the reservation reason. The DB trigger (Layer 4) is the
      // hard guard; this is purely UX so the click doesn't open a wizard.
      if (isLeagueMatchOverlap(courtId, snappedDate, probeEnd)) {
        toast.info(t("leagueMatchTooltip"));
        return;
      }

      // Check gap blocked
      if (gapBlockedSlots.has(`${courtId}-${snapped}`)) {
        toast.error(t("gapError"));
        return;
      }

      if (multiSelectEnabled && onMultiSelectToggle) {
        onMultiSelectToggle(courtId, snappedDate);
      } else {
        onSlotClick(courtId, snappedDate);
      }
    },
    [hallHours.start, hourWidth, timeStepMinutes, selectedDate, getCourtBookings, onSlotClick, gapBlockedSlots, multiSelectEnabled, onMultiSelectToggle, probeDurationMinutes, isLeagueMatchOverlap, t]
  );

  // Drag-to-select handlers for multi-select mode
  const handleDragMouseDown = useCallback(
    (courtId: string, e: React.MouseEvent<HTMLDivElement>) => {
      if (!multiSelectEnabled || !onMultiSelectDragStart || !onMultiSelectToggle) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const result = snapXToSlot(courtId, e.clientX, rect);
      if (!result || result.isBooked) return;
      e.preventDefault();
      onMultiSelectDragStart(courtId, result.snappedDate);
      onMultiSelectToggle(courtId, result.snappedDate);
    },
    [multiSelectEnabled, onMultiSelectDragStart, onMultiSelectToggle, snapXToSlot]
  );

  const handleDragMouseEnter = useCallback(
    (courtId: string, e: React.MouseEvent<HTMLDivElement>) => {
      if (!multiSelectIsDragging || !onMultiSelectDragContinue) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const result = snapXToSlot(courtId, e.clientX, rect);
      if (!result || result.isBooked) return;
      onMultiSelectDragContinue(courtId, result.snappedDate);
    },
    [multiSelectIsDragging, onMultiSelectDragContinue, snapXToSlot]
  );

  const handleDragMouseMove = useCallback(
    (courtId: string, e: React.MouseEvent<HTMLDivElement>) => {
      if (!multiSelectIsDragging || !onMultiSelectDragContinue) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const result = snapXToSlot(courtId, e.clientX, rect);
      if (!result || result.isBooked) return;
      onMultiSelectDragContinue(courtId, result.snappedDate);
    },
    [multiSelectIsDragging, onMultiSelectDragContinue, snapXToSlot]
  );

  const handleTouchStart = useCallback(
    (courtId: string, e: React.TouchEvent<HTMLDivElement>) => {
      if (!multiSelectEnabled || !onMultiSelectDragStart || !onMultiSelectToggle) return;
      const touch = e.touches[0];
      const rect = e.currentTarget.getBoundingClientRect();
      const result = snapXToSlot(courtId, touch.clientX, rect);
      if (!result || result.isBooked) return;
      e.preventDefault(); // prevent scroll
      onMultiSelectDragStart(courtId, result.snappedDate);
      onMultiSelectToggle(courtId, result.snappedDate);
    },
    [multiSelectEnabled, onMultiSelectDragStart, onMultiSelectToggle, snapXToSlot]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!multiSelectIsDragging || !onMultiSelectDragContinue) return;
      e.preventDefault();
      const touch = e.touches[0];
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      if (!el) return;
      // Find the timeline row element with data-court-id
      const row = el.closest("[data-court-id]") as HTMLElement | null;
      if (!row) return;
      const courtId = row.dataset.courtId;
      if (!courtId) return;
      const rect = row.getBoundingClientRect();
      const result = snapXToSlot(courtId, touch.clientX, rect);
      if (!result || result.isBooked) return;
      onMultiSelectDragContinue(courtId, result.snappedDate);
    },
    [multiSelectIsDragging, onMultiSelectDragContinue, snapXToSlot]
  );

  const handleTouchEnd = useCallback(
    () => {
      if (onMultiSelectDragEnd) onMultiSelectDragEnd();
    },
    [onMultiSelectDragEnd]
  );

  // Handle click on booked slot to join waitlist
  const handleBookedClick = useCallback(
    (booking: BookingInfo, e: React.MouseEvent) => {
      e.stopPropagation();
      if (booking.user_id === userId) return;
      if (!onJoinWaitlist) return;
      
      const startTime = new Date(booking.start_time);
      const endTime = new Date(booking.end_time);
      
      if (isBefore(startTime, new Date())) return;

      onJoinWaitlist(booking.court_id, startTime, endTime);
    },
    [userId, onJoinWaitlist]
  );

  return (
    <Card ref={containerRef}>
      <CardContent className="p-0 overflow-hidden">
        <div
          ref={scrollRef}
          className={cn(
            "overflow-y-auto max-h-[65vh]",
            "overflow-x-auto",
            isMobile && "touch-pan-x touch-pan-y"
          )}
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <div className="relative" style={{ minWidth: isMobile ? totalWidth + courtLabelWidth : undefined }}>
            {/* Header row with hour labels + prices */}
            <div className="flex sticky top-0 z-20 bg-card border-b">
              <div className="w-20 lg:w-32 shrink-0 border-r px-2 py-2 text-xs font-medium text-muted-foreground sticky left-0 bg-card z-30">
                {t("courtLabel")}
              </div>
              <div className="relative" style={{ width: totalWidth }}>
                {hourLabels.map((h) => {
                  const priceInfo = slotPrices.get(h.hour * 60);
                  return (
                    <div
                      key={h.hour}
                      className="absolute top-0 h-full border-l border-border pl-1 py-1 font-mono group/hour"
                      style={{ left: (h.hour - hallHours.start) * hourWidth, width: hourWidth }}
                    >
                      <span className="text-[10px] text-muted-foreground leading-none">{h.label}</span>
                      {priceInfo != null && (
                        // G-043: price visible on touch. Hover-capable pointers keep
                        // it hover-revealed (uncluttered); touch (no hover) shows it.
                        <div className="flex [@media(hover:hover)]:hidden [@media(hover:hover)]:group-hover/hour:flex items-center gap-0.5 absolute top-5 left-1 bg-card/95 backdrop-blur rounded px-1 py-0.5 shadow-sm border z-30">
                          <span className={cn(
                            "text-[9px] font-medium whitespace-nowrap",
                            priceInfo.isPeak ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"
                          )}>
                            {priceInfo.perHour} kr
                          </span>
                          {priceInfo.isPeak && (
                            <span className="text-[7px] font-semibold text-amber-600 dark:text-amber-400 uppercase">
                              peak
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Court rows */}
            {courts.map((court) => {
              const courtBookings = getCourtBookings(court.id);
              return (
                <div key={court.id} className="flex border-b" style={{ height: rowHeight }}>
                  {/* Court name + UPPDRAG-15 sponsor stack */}
                  <div className="w-20 lg:w-32 shrink-0 border-r px-2 flex items-center justify-between gap-1 sticky left-0 bg-card z-10">
                    <span className="text-xs font-semibold truncate">{court.name}</span>
                    <CourtSponsorStack sponsors={courtSponsors?.[court.id] ?? []} />
                  </div>

                  {/* Timeline area */}
                  <div
                    className={cn(
                      // T3: legenden lovar "Ledig = grön" — tonen fanns bara i legenden,
                      // aldrig på ytan. Samma nivå som admin-cellen
                      // (ScheduleGridCell.tsx: bg-court-free/5). Past-overlayn nedan
                      // täcker den passerade delen, så tonen läser som "bokningsbar".
                      "relative cursor-pointer bg-court-free/5",
                      multiSelectEnabled && "select-none",
                    )}
                    style={{ width: totalWidth }}
                    data-court-id={court.id}
                    onClick={(e) => {
                      // In multi-select drag mode, click is handled by mousedown
                      if (multiSelectEnabled && onMultiSelectDragStart) return;
                      handleRowClick(court.id, e);
                    }}
                    onMouseDown={multiSelectEnabled ? (e) => handleDragMouseDown(court.id, e) : undefined}
                    onMouseEnter={multiSelectIsDragging ? (e) => handleDragMouseEnter(court.id, e) : undefined}
                    onMouseMove={multiSelectIsDragging ? (e) => handleDragMouseMove(court.id, e) : undefined}
                    onTouchStart={multiSelectEnabled ? (e) => handleTouchStart(court.id, e) : undefined}
                    onTouchMove={multiSelectEnabled ? handleTouchMove : undefined}
                    onTouchEnd={multiSelectEnabled ? handleTouchEnd : undefined}
                  >
                    {/* Hour grid lines */}
                    {hourLabels.map((h) => (
                      <div
                        key={h.hour}
                        className="absolute top-0 h-full border-l border-border/40"
                        style={{ left: (h.hour - hallHours.start) * hourWidth }}
                      />
                    ))}

                    {/* Half-hour dotted lines */}
                    {hourLabels.map((h) => (
                      <div
                        key={`half-${h.hour}`}
                        className="absolute top-0 h-full border-l border-dashed border-border/20"
                        style={{ left: (h.hour - hallHours.start) * hourWidth + hourWidth / 2 }}
                      />
                    ))}

                    {/* Past time overlay */}
                    {(() => {
                      const now = new Date();
                      const dayStart = new Date(selectedDate);
                      dayStart.setHours(hallHours.start, 0, 0, 0);
                      if (now > dayStart) {
                        const pastWidth = Math.min(timeToX(now), totalWidth);
                        if (pastWidth > 0) {
                          return (
                            <div
                              // T3: var bg-muted/50 + pointer-events-none — osynlig och utan
                          // pekar-affordance. `--muted` (96 % ljushet) mot `--background`
                          // (98 %) ger ~1 % skillnad efter /50; muted-foreground fungerar i
                          // BÅDA temana (mörknar i ljust, ljusnar i mörkt). pointer-events
                          // släpps på så cursor-not-allowed gäller; klicket bubblar vidare
                          // till lanens onClick och möter den befintliga tidsspärren.
                          className="absolute top-0 h-full bg-muted-foreground/15 cursor-not-allowed z-[1]"
                              style={{ left: 0, width: Math.max(0, pastWidth) }}
                            />
                          );
                        }
                      }
                      return null;
                    })()}

                    {/* Current time indicator */}
                    {(() => {
                      const now = new Date();
                      const x = timeToX(now);
                      if (x >= 0 && x <= totalWidth && selectedDate.toDateString() === now.toDateString()) {
                        return (
                          <div
                            className="absolute top-0 h-full w-0.5 bg-destructive z-[5] pointer-events-none"
                            style={{ left: x }}
                          />
                        );
                      }
                      return null;
                    })()}

                    {/* Booking blocks */}
                    {courtBookings.map((b) => {
                      const bStart = new Date(b.start_time);
                      const bEnd = new Date(b.end_time);
                      const x = timeToX(bStart);
                      const w = timeToX(bEnd) - x;
                      const mine = b.user_id === userId;
                      const isActivity = b.is_open_match && b.status === "confirmed";
                      const canWaitlist = !mine && !isActivity && !isBefore(bStart, new Date()) && onJoinWaitlist;
                      const isClickable = mine || isActivity || canWaitlist;

                      const handleClick = (e: React.MouseEvent) => {
                        e.stopPropagation();
                        if ((mine || isActivity) && onBookingClick) {
                          onBookingClick(b);
                        } else if (canWaitlist) {
                          handleBookedClick(b, e);
                        }
                      };

                      // Label for the block
                      const hasActivity = !!b.activity_id;
                      let label: string;
                      if (mine && isActivity) {
                        label = `${t("mine")}: ${hasActivity ? t("activities:activity", "Aktivitet") : t("open")}`;
                      } else if (mine) {
                        label = t("mine");
                      } else if (isActivity) {
                        label = hasActivity ? t("activities:activity", "Aktivitet") : t("activities:type.openMatch", t("open"));
                      } else if (canWaitlist) {
                        label = t("bookedQueue");
                      } else {
                        label = t("booked");
                      }

                      const fullTitle = `${label} ${format(bStart, "HH:mm")}–${format(bEnd, "HH:mm")}`;

                      return (
                        <div
                          key={b.id}
                          className={cn(
                            "absolute top-1 rounded-md border flex items-center px-2 text-[10px] font-medium z-[3]",
                            mine
                              ? "bg-court-mine/25 border-court-mine/40 text-court-mine cursor-pointer hover:bg-court-mine/35 transition-colors"
                              : isActivity
                              ? "bg-court-partial/20 border-court-partial/40 text-court-partial cursor-pointer hover:bg-court-partial/30 transition-colors"
                              : canWaitlist
                              ? "bg-court-booked/15 border-court-booked/30 text-court-booked cursor-pointer hover:bg-court-booked/25 transition-colors"
                              : "bg-court-booked/15 border-court-booked/30 text-court-booked pointer-events-none"
                          )}
                          style={{
                            left: Math.max(0, x),
                            width: Math.max(24, w),
                            height: rowHeight - 8,
                          }}
                          onClick={isClickable ? handleClick : undefined}
                          title={fullTitle}
                        >
                          <span className="truncate">
                            {label}{" "}
                            {format(bStart, "HH:mm")}–{format(bEnd, "HH:mm")}
                          </span>
                        </div>
                      );
                    })}

                    {/* TASK-75 PR-D Layer 1 — placed-league-match overlay.
                        Distinct purple/indigo styling separates this from
                        regular bookings (court-booked palette). The block
                        spans the full match window in a single absolute
                        div, so the "first vs mid" pattern from a
                        column-based grid doesn't apply here — the
                        overlapping slots underneath already get their click
                        intercepted by handleRowClick / snapXToSlot. */}
                    {getCourtLeagueMatches(court.id).map((m) => {
                      const x = timeToX(m.start);
                      const w = timeToX(m.end) - x;
                      const fullTitle = `${t("leagueMatchSlot")} ${format(m.start, "HH:mm")}–${format(m.end, "HH:mm")} — ${t("leagueMatchTooltip")}`;
                      return (
                        <div
                          key={`lm-${m.id}`}
                          className={cn(
                            "absolute top-1 rounded-md border flex items-center px-2 text-[10px] font-medium",
                            // z-[3] matches booking blocks so they don't fight
                            // with the past-time / current-time overlays.
                            "z-[3] cursor-not-allowed pointer-events-none",
                            "bg-purple-200/40 dark:bg-purple-900/20 border-purple-300 dark:border-purple-800",
                            "text-purple-700 dark:text-purple-300",
                          )}
                          style={{
                            left: Math.max(0, x),
                            width: Math.max(24, w),
                            height: rowHeight - 8,
                          }}
                          title={fullTitle}
                        >
                          <span className="truncate">
                            {t("leagueMatchSlot")}{" "}
                            {format(m.start, "HH:mm")}–{format(m.end, "HH:mm")}
                          </span>
                        </div>
                      );
                    })}

                    {/* Single-booking selected slot highlights */}
                    {!multiSelectEnabled && selectedSlots.map((slot, idx) => {
                      if (slot.courtId !== court.id) return null;
                      const x = timeToX(slot.startTime);
                      const stepW = (timeStepMinutes / 60) * hourWidth;
                      return (
                        <div
                          key={`sel-${idx}`}
                          className="absolute top-0.5 rounded-md border-2 border-primary bg-primary/10 z-[4] pointer-events-none flex items-center justify-center"
                          style={{ left: Math.max(0, x), width: Math.max(24, stepW), height: rowHeight - 4 }}
                        >
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                            {idx + 1}
                          </span>
                        </div>
                      );
                    })}

                    {/* Multi-select mode highlights */}
                    {multiSelectEnabled && multiSelectedSlots.map((slot, idx) => {
                      if (slot.courtId !== court.id) return null;
                      const slotStart = new Date(slot.startTime);
                      const x = timeToX(slotStart);
                      const slotEnd = new Date(slot.endTime);
                      const w = timeToX(slotEnd) - x;
                      return (
                        <div
                          key={`msel-${idx}`}
                          className="absolute top-0.5 rounded-md border-2 border-primary bg-primary/20 z-[4] pointer-events-none flex items-center justify-center"
                          style={{ left: Math.max(0, x), width: Math.max(24, w), height: rowHeight - 4 }}
                        >
                          <CheckSquare className="h-4 w-4 text-primary" />
                        </div>
                      );
                    })}

                    {/* At-limit overlay: grey out unselected free slots */}
                    {multiSelectEnabled && multiSelectIsAtLimit && (() => {
                      const selectedSet = new Set(
                        multiSelectedSlots
                          .filter((s) => s.courtId === court.id)
                          .map((s) => new Date(s.startTime).getTime())
                      );
                      const totalSlotCount = totalHours * (60 / timeStepMinutes);
                      const overlaySlots: React.ReactNode[] = [];
                      for (let i = 0; i < totalSlotCount; i++) {
                        const slotMinutes = hallHours.start * 60 + i * timeStepMinutes;
                        const slotDate = new Date(selectedDate);
                        slotDate.setHours(Math.floor(slotMinutes / 60), slotMinutes % 60, 0, 0);
                        if (selectedSet.has(slotDate.getTime())) continue;
                        // Check if booked — skip overlay on already-booked slots
                        const isBooked = courtBookings.some((b) => {
                          const bStart = new Date(b.start_time);
                          const bEnd = new Date(b.end_time);
                          return slotDate >= bStart && slotDate < bEnd;
                        });
                        if (isBooked) continue;
                        const x = ((slotMinutes / 60) - hallHours.start) * hourWidth;
                        const stepWidth = (timeStepMinutes / 60) * hourWidth;
                        overlaySlots.push(
                          <div
                            key={`limit-${i}`}
                            className="absolute top-0 h-full opacity-30 pointer-events-none z-[6] bg-muted"
                            style={{ left: x, width: stepWidth }}
                          />
                        );
                      }
                      return overlaySlots;
                    })()}

                    {/* Gap-blocked slot indicators */}
                    {gapProtection?.enabled && Array.from(gapBlockedSlots).filter((key) => key.startsWith(court.id + "-")).map((key) => {
                      const slotMin = parseInt(key.split("-").pop()!);
                      const slotHour = slotMin / 60;
                      const x = (slotHour - hallHours.start) * hourWidth;
                      const stepWidth = (timeStepMinutes / 60) * hourWidth;
                      return (
                        <TooltipProvider key={key}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                className="absolute top-0 h-full bg-destructive/8 z-[2] cursor-not-allowed flex items-center justify-center"
                                style={{ left: x, width: stepWidth }}
                              >
                                <AlertCircle className="h-3 w-3 text-destructive/40" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">
                              {t("gapTooltip")}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── UPPDRAG-15: court-sponsor stack ──────────────────────────────────
//
// Renders up to 2 sponsor logos stacked horizontally, with a "+N"
// badge for any additional sponsors. Each logo is clickable when
// click_url is set (opens in a new tab). Active-window filtering is
// done by the parent (BookingGridProps.courtSponsors map already
// excludes inactive entries).

function CourtSponsorStack({ sponsors }: { sponsors: CourtSponsorRow[] }) {
  // Defensive re-sort (caller should already pass active+sorted, but
  // sorting twice is cheap and prevents a buggy callsite from rendering
  // the stack in the wrong order).
  const sorted = useMemo(() => activeSponsorsSorted(sponsors), [sponsors]);
  if (sorted.length === 0) return null;

  const visible = sorted.slice(0, 2);
  const overflow = sorted.length - visible.length;

  return (
    <div className="flex items-center gap-0.5 shrink-0">
      {visible.map((s) => (
        <SponsorIcon key={s.id} sponsor={s} />
      ))}
      {overflow > 0 && (
        <span className="text-[9px] font-medium text-muted-foreground bg-muted px-1 rounded">
          +{overflow}
        </span>
      )}
    </div>
  );
}

function SponsorIcon({ sponsor }: { sponsor: CourtSponsorRow }) {
  const img = (
    <img
      src={sponsor.logo_url}
      alt={sponsor.sponsor_name ?? "Sponsor"}
      className="h-4 w-4 object-contain"
      // Failing-image fallback: hide rather than show broken icon
      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
    />
  );

  if (sponsor.click_url) {
    return (
      <a
        href={sponsor.click_url}
        target="_blank"
        rel="noopener noreferrer"
        title={sponsor.sponsor_name ?? "Sponsor"}
        className="hover:opacity-70 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        {img}
      </a>
    );
  }
  return <span title={sponsor.sponsor_name ?? "Sponsor"}>{img}</span>;
}